// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect } from 'react';
import { Input, Textarea } from '@/components/ui';
import { Field, FormDialog } from '@/components/forms';
import type { OrganizationResponse } from '../../types/workspace';

interface EditOrganizationDialogProps {
  organization: OrganizationResponse | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (organization: OrganizationResponse | null, data: { orgId?: string; name: string; description?: string }) => void;
  isPending: boolean;
}

export function EditOrganizationDialog({
  organization,
  open,
  onOpenChange,
  onSave,
  isPending,
}: EditOrganizationDialogProps) {

  const [formData, setFormData] = useState({
    orgId: '',
    name: '',
    description: '',
  });

  useEffect(() => {
    if (organization) {
      setFormData({
        orgId: organization.orgId,
        name: organization.name,
        description: organization.description || '',
      });
    } else {
      setFormData({
        orgId: '',
        name: '',
        description: '',
      });
    }
  }, [organization]);

  const handleSubmit = () => {
    if (!formData.name.trim()) return;
    onSave(organization, {
      orgId: !organization && formData.orgId.trim() ? formData.orgId.trim() : undefined,
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
    });
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={organization ? 'Edit Organization' : 'Create Organization'}
      description={
        organization
          ? 'Update the organization details below.'
          : 'Fill in the details to create a new organization.'
      }
      onSubmit={handleSubmit}
      submitting={isPending}
      canSubmit={formData.name.trim().length > 0}
      submitLabel={organization ? 'Save Changes' : 'Create Organization'}
      size="md"
    >
      <Field label="Organization ID" htmlFor="org_id">
        <Input
          id="org_id"
          value={organization ? organization.orgId : formData.orgId}
          onChange={(e) => setFormData(prev => ({ ...prev, orgId: e.target.value }))}
          disabled={!!organization}
          className={organization ? "bg-muted" : ""}
          placeholder={organization ? "" : "Enter organization ID (optional)"}
        />
      </Field>
      <Field label="Organization Name" htmlFor="name">
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Organization name"
          required
        />
      </Field>
      <Field label="Description (Optional)" htmlFor="description">
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Describe the organization's purpose"
          rows={3}
        />
      </Field>
    </FormDialog>
  );
}
